import axios from 'axios';
import { Application, ApplicationFormData, Permission, PermissionFormData, Role, RoleFormData } from '../types/rbac.types';
import { API_URL } from '../utils/ConfigUtils';

// Create axios instance with base URL
const axiosInstance = axios.create({
  baseURL: API_URL,
});

// Add request interceptor for JWT token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if error is due to token expiration
    if (error.response?.status === 401) {
      console.log("Authentication error: Please log in again");
      // Redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Application APIs
export const getApplications = async (status?: string): Promise<Application[]> => {
  try {
    const params = status ? { status } : {};
    const response = await axiosInstance.get('/applications/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching applications:', error);
    throw error;
  }
};

export const getApplication = async (id: string): Promise<Application> => {
  const response = await axiosInstance.get(`/applications/${id}`);
  return response.data;
};

export const createApplication = async (data: ApplicationFormData): Promise<Application> => {
  // Process data to ensure empty strings are converted to null for URL fields
  const processedData = {
    ...data,
    app_url: data.app_url && data.app_url.trim() ? data.app_url.trim() : null,
    api_url: data.api_url && data.api_url.trim() ? data.api_url.trim() : null,
    logo_url: data.logo_url && data.logo_url.trim() ? data.logo_url.trim() : null,
  };
  
  const response = await axiosInstance.post('/applications/', processedData);
  return response.data.application;
};

export const updateApplication = async (id: string, data: Partial<ApplicationFormData>): Promise<Application> => {
  // Process data to ensure empty strings are converted to null for URL fields
  const processedData = {
    ...data,
    app_url: data.app_url && data.app_url.trim() ? data.app_url.trim() : null,
    api_url: data.api_url && data.api_url.trim() ? data.api_url.trim() : null,
    logo_url: data.logo_url && data.logo_url.trim() ? data.logo_url.trim() : null,
  };
  
  const response = await axiosInstance.put(`/applications/${id}`, processedData);
  return response.data.application;
};

export const updateApplicationStatus = async (id: string, status: 'active' | 'inactive' | 'development'): Promise<Application> => {
  const response = await axiosInstance.patch(`/applications/${id}/status`, { status });
  return response.data.application;
};

export const deleteApplication = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/applications/${id}`);
};

export const getPermissionCategories = async (applicationId: string): Promise<string[]> => {
  try {
    const response = await axiosInstance.get(`/applications/${applicationId}/permission-categories`);
    return response.data.permission_categories || [];
  } catch (error) {
    console.error('Error fetching permission categories:', error);
    // Return default categories if the API fails
    return ['access', 'read', 'write', 'delete', 'admin', 'system'];
  }
};

export const updatePermissionCategories = async (applicationId: string, categories: string[]): Promise<string[]> => {
  const response = await axiosInstance.put(`/applications/${applicationId}/permission-categories`, {
    permission_categories: categories
  });
  return response.data.permission_categories;
};

// Permission APIs
export const getPermissions = async (applicationId?: string, category?: string): Promise<Permission[]> => {
  const params: Record<string, string> = {};
  if (applicationId) params.application_id = applicationId;
  if (category) params.category = category;
  
  const response = await axiosInstance.get('/permissions/', { params });
  return response.data;
};

export const getPermission = async (id: string): Promise<Permission> => {
  const response = await axiosInstance.get(`/permissions/${id}`);
  return response.data;
};

export const createPermission = async (data: PermissionFormData): Promise<Permission> => {
  const response = await axiosInstance.post('/permissions/', data);
  return response.data.permission;
};

export const updatePermission = async (id: string, data: Partial<PermissionFormData>): Promise<Permission> => {
  const response = await axiosInstance.put(`/permissions/${id}`, data);
  return response.data.permission;
};

export const deletePermission = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/permissions/${id}`);
};

// Role APIs
export const getRoles = async (applicationId?: string, includePermissions: boolean = false): Promise<Role[]> => {
  try {
    const params: Record<string, string | boolean> = { include_permissions: includePermissions };
    if (applicationId && applicationId.trim() !== '') params.application_id = applicationId;
    
    const response = await axiosInstance.get('/roles/', { params });
    return response.data || [];
  } catch (error) {
    console.error('Error fetching roles:', error);
    // Return empty array if the API fails
    return [];
  }
};

export const getRole = async (id: string, includePermissions: boolean = true): Promise<Role> => {
  const response = await axiosInstance.get(`/roles/${id}`, { 
    params: { include_permissions: includePermissions } 
  });
  return response.data;
};

export const createRole = async (data: RoleFormData): Promise<Role> => {
  // If application_id is empty string, remove it to make it a global role
  const roleData = {...data};
  if (roleData.application_id === '') {
    delete roleData.application_id;
  }
  
  const response = await axiosInstance.post('/roles/', roleData);
  return response.data.role;
};

export const updateRole = async (id: string, data: Partial<RoleFormData>): Promise<Role> => {
  // If application_id is empty string, remove it to make it a global role
  const roleData = {...data};
  if (roleData.application_id === '') {
    delete roleData.application_id;
  }
  
  const response = await axiosInstance.put(`/roles/${id}`, roleData);
  return response.data.role;
};

export const deleteRole = async (id: string): Promise<{success: boolean, message?: string, details?: any}> => {
  try {
    const response = await axiosInstance.delete(`/roles/${id}`);
    return { 
      success: true, 
      message: response.data?.message || "Role deleted successfully" 
    };
  } catch (error: any) {
    console.error('Error deleting role:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.response?.data?.error || "Failed to delete role",
      details: error.response?.data || null
    };
  }
};

export const addPermissionToRole = async (roleId: string, permissionId: string): Promise<Role> => {
  const response = await axiosInstance.post(`/roles/${roleId}/permissions`, { permission_id: permissionId });
  return response.data.role;
};

export const removePermissionFromRole = async (roleId: string, permissionId: string): Promise<Role> => {
  const response = await axiosInstance.delete(`/roles/${roleId}/permissions/${permissionId}`);
  return response.data.role;
};

export const addPermissionsToRoleBulk = async (roleId: string, permissionIds: string[]): Promise<{role: Role, added_count: number, skipped_existing: number, invalid_ids: string[]}> => {
  const response = await axiosInstance.post(`/roles/${roleId}/permissions/bulk`, { permission_ids: permissionIds });
  return {
    role: response.data.role,
    added_count: response.data.added_count,
    skipped_existing: response.data.skipped_existing,
    invalid_ids: response.data.invalid_ids || []
  };
};

export const removePermissionsFromRoleBulk = async (roleId: string, permissionIds: string[]): Promise<{role: Role, removed_count: number}> => {
  const response = await axiosInstance.delete(`/roles/${roleId}/permissions/bulk`, { data: { permission_ids: permissionIds } });
  return {
    role: response.data.role,
    removed_count: response.data.removed_count
  };
};

// User-Role APIs
export const getUserRoles = async (userId: string): Promise<Role[]> => {
  const response = await axiosInstance.get(`/users/${userId}`, { 
    params: { include_roles: true } 
  });
  return response.data.roles || [];
};

export const addRoleToUser = async (userId: string, roleId: string): Promise<void> => {
  await axiosInstance.post(`/users/${userId}/roles`, { role_id: roleId });
};

export const removeRoleFromUser = async (userId: string, roleId: string): Promise<void> => {
  await axiosInstance.delete(`/users/${userId}/roles/${roleId}`);
};

export const getUserPermissions = async (userId: string): Promise<Permission[]> => {
  const response = await axiosInstance.get(`/users/${userId}/permissions`);
  return response.data.permissions || [];
}; 